import { useState, useEffect } from 'react';
import { Badge } from '../components/ui/badge';
import { AlertTriangle, Link2, FlaskConical, FileText, Shield, MapPin, Tag } from 'lucide-react';

const FLAG_COLORS = {
  HIGH_PRIORITY: { bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-700 dark:text-red-300', border: 'border-red-300 dark:border-red-700' },
  PRIORITY_REVIEW: { bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-700 dark:text-red-300', border: 'border-red-300 dark:border-red-700' },
  DNA_ACTION_NEEDED: { bg: 'bg-amber-100 dark:bg-amber-900/40', text: 'text-amber-700 dark:text-amber-300', border: 'border-amber-300 dark:border-amber-700' },
  DNA_COMPARISON_NEEDED: { bg: 'bg-amber-100 dark:bg-amber-900/40', text: 'text-amber-700 dark:text-amber-300', border: 'border-amber-300 dark:border-amber-700' },
  OCME_CONTACT_NEEDED: { bg: 'bg-amber-100 dark:bg-amber-900/40', text: 'text-amber-700 dark:text-amber-300', border: 'border-amber-300 dark:border-amber-700' },
  CRUTCHLEY_VICTIM_CONFIRMED: { bg: 'bg-purple-100 dark:bg-purple-900/40', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-300 dark:border-purple-700' },
  CRUTCHLEY_VICTIM_PROBABLE: { bg: 'bg-purple-100 dark:bg-purple-900/40', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-300 dark:border-purple-700' },
  CRUTCHLEY_INVESTIGATION: { bg: 'bg-purple-100 dark:bg-purple-900/40', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-300 dark:border-purple-700' },
  LINKED_PERPETRATOR: { bg: 'bg-purple-100 dark:bg-purple-900/40', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-300 dark:border-purple-700' },
  POTENTIAL_HEUERMANN_VICTIM: { bg: 'bg-purple-100 dark:bg-purple-900/40', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-300 dark:border-purple-700' },
  GILGO_BEACH_UNIDENTIFIED: { bg: 'bg-purple-100 dark:bg-purple-900/40', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-300 dark:border-purple-700' },
  LISK_VICTIM: { bg: 'bg-purple-100 dark:bg-purple-900/40', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-300 dark:border-purple-700' },
  LINKED_DUMP_SITE: { bg: 'bg-yellow-100 dark:bg-yellow-900/40', text: 'text-yellow-700 dark:text-yellow-300', border: 'border-yellow-300 dark:border-yellow-700' },
  SERIAL_KILLER_CLUSTER: { bg: 'bg-yellow-100 dark:bg-yellow-900/40', text: 'text-yellow-700 dark:text-yellow-300', border: 'border-yellow-300 dark:border-yellow-700' },
  BROOKS_COUNTY_CROSS_BORDER: { bg: 'bg-teal-100 dark:bg-teal-900/40', text: 'text-teal-700 dark:text-teal-300', border: 'border-teal-300 dark:border-teal-700' },
  TIMELINE_POSSIBLE: { bg: 'bg-teal-100 dark:bg-teal-900/40', text: 'text-teal-700 dark:text-teal-300', border: 'border-teal-300 dark:border-teal-700' },
  BEST_CANDIDATE: { bg: 'bg-emerald-100 dark:bg-emerald-900/40', text: 'text-emerald-700 dark:text-emerald-300', border: 'border-emerald-300 dark:border-emerald-700' },
  CLOTHING_MATCH: { bg: 'bg-emerald-100 dark:bg-emerald-900/40', text: 'text-emerald-700 dark:text-emerald-300', border: 'border-emerald-300 dark:border-emerald-700' },
  CONTESTED_MATCH: { bg: 'bg-orange-100 dark:bg-orange-900/40', text: 'text-orange-700 dark:text-orange-300', border: 'border-orange-300 dark:border-orange-700' },
  POTENTIAL_HOMICIDE_BODY_DUMP: { bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-700 dark:text-red-300', border: 'border-red-300 dark:border-red-700' },
  HOMICIDE_CONFIRMED: { bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-700 dark:text-red-300', border: 'border-red-300 dark:border-red-700' },
  NOT_IN_NAMUS: { bg: 'bg-slate-100 dark:bg-slate-800', text: 'text-slate-600 dark:text-slate-400', border: 'border-slate-300 dark:border-slate-600' },
  GEOCODING_ARTIFACT: { bg: 'bg-slate-100 dark:bg-slate-800', text: 'text-slate-600 dark:text-slate-400', border: 'border-slate-300 dark:border-slate-600' },
  EXCLUDED_AGE: { bg: 'bg-slate-100 dark:bg-slate-800', text: 'text-slate-600 dark:text-slate-400', border: 'border-slate-300 dark:border-slate-600' },
};

const DEFAULT_FLAG_STYLE = { bg: 'bg-slate-100 dark:bg-slate-800', text: 'text-slate-600 dark:text-slate-400', border: 'border-slate-300 dark:border-slate-600' };

// Two-tier flag classification
const ACTION_FLAGS = new Set([
  'HIGH_PRIORITY', 'PRIORITY_REVIEW', 'DNA_ACTION_NEEDED', 'DNA_COMPARISON_NEEDED',
  'OCME_CONTACT_NEEDED', 'CRUTCHLEY_VICTIM_CONFIRMED', 'CRUTCHLEY_VICTIM_PROBABLE',
  'LINKED_PERPETRATOR', 'POTENTIAL_HEUERMANN_VICTIM', 'LISK_VICTIM',
  'LINKED_DUMP_SITE', 'SERIAL_KILLER_CLUSTER', 'HOMICIDE_CONFIRMED',
  'POTENTIAL_HOMICIDE_BODY_DUMP', 'BEST_CANDIDATE', 'NAMED_SUSPECT',
  'FEDERAL_REFERRAL_RECOMMENDED',
]);

// GEOCODING_ARTIFACT is rendered separately below the card footer, not as a flag badge
const EXCLUDED_FROM_FLAGS = new Set(['GEOCODING_ARTIFACT']);

const REFERENCE_FLAGS = new Set([
  'CRUTCHLEY_INVESTIGATION', 'GILGO_BEACH_UNIDENTIFIED',
  'BROOKS_COUNTY_CROSS_BORDER', 'TIMELINE_POSSIBLE', 'CLOTHING_MATCH',
  'CONTESTED_MATCH', 'NOT_IN_NAMUS', 'EXCLUDED_AGE',
  'BORROWED_DOCUMENTS', 'TATTOO_IDENTIFIER', 'DENTAL_AVAILABLE', 'TRANSGENDER_POSSIBLE',
]);

const INVESTIGATIVE_FLAGS = new Set([...ACTION_FLAGS, ...REFERENCE_FLAGS]);

export default function InvestigatorPanel({ match, warningCount = 0, leVerified = false }) {
  const matchFlags = match?.flags || [];
  const mpFlags = match?.mp_flags || [];
  const uidFlags = match?.uid_flags || [];

  const allFlags = [...new Set([...matchFlags, ...mpFlags, ...uidFlags])]
    .filter(f => INVESTIGATIVE_FLAGS.has(f) && !EXCLUDED_FROM_FLAGS.has(f));

  const actionFlags = allFlags.filter(f => ACTION_FLAGS.has(f));
  const referenceFlags = allFlags.filter(f => REFERENCE_FLAGS.has(f));

  const hasPriority = allFlags.some(f =>
    ['HIGH_PRIORITY', 'PRIORITY_REVIEW', 'DNA_ACTION_NEEDED', 'DNA_COMPARISON_NEEDED'].includes(f)
  );

  const hasPerpetratorLink = allFlags.some(f =>
    f.includes('CRUTCHLEY') || f.includes('HEUERMANN') ||
    f.includes('LISK') || f === 'LINKED_PERPETRATOR'
  );

  const hasDumpSite = allFlags.some(f =>
    f === 'LINKED_DUMP_SITE' || f === 'SERIAL_KILLER_CLUSTER'
  );

  const hasDnaAction = allFlags.some(f =>
    f === 'DNA_ACTION_NEEDED' || f === 'DNA_COMPARISON_NEEDED'
  );

  // Auto-expand synopsis for priority matches or when warnings exist
  const shouldAutoExpand = hasPriority || hasPerpetratorLink || hasDumpSite || warningCount > 0;
  const [synopsisExpanded, setSynopsisExpanded] = useState(shouldAutoExpand);

  const investigatorNote = match?.investigator_note ||
    match?.mp_investigator_note ||
    match?.uid_investigator_note;

  const linkedPerp = match?.linked_perpetrator;
  const hasRestrictedNotes = match?.has_restricted_notes;
  const hasRestrictedPerp = match?.has_restricted_perpetrator;
  const synopsis = match?.synopsis || match?.ai_synopsis || match?.static_synopsis;
  const dumpSiteCases = match?.uid_linked_cases || [];

  if (!allFlags.length && !investigatorNote && !hasRestrictedNotes && !synopsis) {
    return null;
  }

  return (
    <div data-testid="investigator-panel" className="space-y-3 mt-4 mb-2">

      {hasPriority && (
        <div data-testid="priority-banner" className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800">
          <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0" />
          <span className="text-sm font-semibold text-red-700 dark:text-red-300">
            PRIORITY REVIEW — Immediate investigator action recommended
          </span>
        </div>
      )}

      {hasPerpetratorLink && linkedPerp && leVerified && (
        <div data-testid="perpetrator-banner" className="flex items-center gap-2 px-3 py-2 rounded-lg bg-purple-50 dark:bg-purple-950/40 border border-purple-200 dark:border-purple-800">
          <Link2 className="w-4 h-4 text-purple-600 dark:text-purple-400 flex-shrink-0" />
          <span className="text-sm font-semibold text-purple-700 dark:text-purple-300">
            LINKED TO KNOWN SUSPECT: {linkedPerp}
          </span>
        </div>
      )}
      {(hasPerpetratorLink || hasRestrictedPerp) && !leVerified && (
        <div data-testid="perpetrator-banner-restricted" className="flex items-center gap-2 px-3 py-2 rounded-lg bg-purple-50/50 dark:bg-purple-950/20 border border-purple-200/50 dark:border-purple-800/50">
          <Shield className="w-4 h-4 text-purple-400 flex-shrink-0" />
          <span className="text-sm text-purple-500 dark:text-purple-400">
            Linked to an active investigation — <span className="font-medium">details restricted to verified law enforcement</span>
          </span>
        </div>
      )}

      {hasDumpSite && (
        <div data-testid="dump-site-banner" className="flex items-center gap-2 px-3 py-2 rounded-lg bg-yellow-50 dark:bg-yellow-950/40 border border-yellow-200 dark:border-yellow-800">
          <MapPin className="w-4 h-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
          <span className="text-sm font-semibold text-yellow-700 dark:text-yellow-300">
            LINKED DUMP SITE — {dumpSiteCases.length > 0 ? `${dumpSiteCases.length + 1} bodies at this location` : 'Multiple remains found at same site'}
          </span>
        </div>
      )}

      {hasDnaAction && (
        <div data-testid="dna-action-box" className="flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800">
          <FlaskConical className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
          <span className="text-sm text-amber-700 dark:text-amber-300">
            <span className="font-semibold">DNA Action Required</span> — DNA comparison recommended for this match. See investigator note for contacts.
          </span>
        </div>
      )}

      {allFlags.length > 0 && (
        <div data-testid="flags-section" className="space-y-1.5">
          {/* Action flags - prominent */}
          {actionFlags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {actionFlags.map(flag => {
                const style = FLAG_COLORS[flag] || DEFAULT_FLAG_STYLE;
                return (
                  <span
                    key={flag}
                    data-testid={`flag-${flag.toLowerCase().replace(/_/g, '-')}`}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border ${style.bg} ${style.text} ${style.border}`}
                  >
                    <AlertTriangle className="w-3.5 h-3.5" />
                    {flag.replace(/_/g, ' ')}
                  </span>
                );
              })}
            </div>
          )}
          {/* Reference flags - subdued */}
          {referenceFlags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {referenceFlags.map(flag => (
                <span
                  key={flag}
                  data-testid={`flag-${flag.toLowerCase().replace(/_/g, '-')}`}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-normal text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700"
                >
                  <Tag className="w-2.5 h-2.5" />
                  {flag.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {synopsis && (
        <div data-testid="synopsis-section" className="rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50/50 dark:bg-indigo-950/20">
          <button
            onClick={() => setSynopsisExpanded(!synopsisExpanded)}
            className="w-full p-3 text-left flex items-center justify-between"
          >
            <h4 className="font-semibold text-sm text-indigo-700 dark:text-indigo-300 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Match Synopsis
            </h4>
            <span className="text-xs text-indigo-500">{synopsisExpanded ? 'collapse' : 'expand'}</span>
          </button>
          {synopsisExpanded && (
            <div className="px-3 pb-3">
              <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-line">{synopsis}</p>
            </div>
          )}
        </div>
      )}

      {investigatorNote && leVerified && (
        <div data-testid="investigator-note" className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
          <div className="p-3">
            <h4 className="font-semibold text-sm text-slate-700 dark:text-slate-300 flex items-center gap-2 mb-2">
              <Shield className="w-4 h-4 text-blue-500" />
              Investigator Note
            </h4>
            <p className="text-sm text-slate-600 dark:text-slate-400 whitespace-pre-line leading-relaxed">
              {investigatorNote}
            </p>
          </div>
        </div>
      )}
      {(hasRestrictedNotes || (investigatorNote && !leVerified)) && (
        <div data-testid="investigator-note-restricted" className="rounded-lg border border-slate-200/50 dark:border-slate-700/50 bg-slate-50/50 dark:bg-slate-800/30">
          <div className="p-3 flex items-center gap-2">
            <Shield className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <p className="text-xs text-slate-400">
              Investigator notes available to verified law enforcement accounts.
              <a href="/verify-organization" className="ml-1 text-blue-500 hover:text-blue-600 font-medium">Verify your affiliation</a>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
